/**
 * Circuit Breaker Pattern for tRPC Client
 *
 * Prevents thundering herd problem by stopping requests when backend is down.
 * Implements three states:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Backend is down, requests fail immediately
 * - HALF_OPEN: Testing if backend has recovered
 *
 * @module lib/trpc/circuit-breaker
 * @see Issue #XXX - Implement Circuit Breaker for Backend Failures
 */

export type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

export interface CircuitBreakerOptions {
  /** Number of consecutive failures before opening circuit */
  failureThreshold: number;
  /** Time in ms to wait before trying again (OPEN → HALF_OPEN) */
  resetTimeout: number;
  /** Number of successful requests to close circuit from HALF_OPEN */
  successThreshold: number;
}

/**
 * Circuit Breaker to prevent thundering herd when backend fails
 *
 * Usage:
 * ```ts
 * const breaker = new CircuitBreaker({ failureThreshold: 5, resetTimeout: 30000, successThreshold: 2 });
 *
 * if (breaker.isOpen()) {
 *   throw new Error('Circuit breaker is OPEN - backend is down');
 * }
 *
 * try {
 *   const result = await apiCall();
 *   breaker.recordSuccess();
 *   return result;
 * } catch (error) {
 *   breaker.recordFailure();
 *   throw error;
 * }
 * ```
 */
export class CircuitBreaker {
  private state: CircuitState = 'CLOSED';
  private failureCount = 0;
  private successCount = 0;
  private nextAttempt = 0;
  private readonly options: CircuitBreakerOptions;
  private stateChangeListeners: Array<(state: CircuitState) => void> = [];

  constructor(options: Partial<CircuitBreakerOptions> = {}) {
    this.options = {
      failureThreshold: options.failureThreshold ?? 5,
      resetTimeout: options.resetTimeout ?? 30000,
      successThreshold: options.successThreshold ?? 2,
    };
  }

  /**
   * Check if circuit is open (backend is down)
   */
  isOpen(): boolean {
    if (this.state === 'OPEN') {
      // Check if we should transition to HALF_OPEN
      if (Date.now() >= this.nextAttempt) {
        this.transitionTo('HALF_OPEN');
        return false;
      }
      return true;
    }
    return false;
  }

  /**
   * Record successful request
   */
  recordSuccess(): void {
    if (this.state === 'HALF_OPEN') {
      this.successCount++;
      if (this.successCount >= this.options.successThreshold) {
        this.transitionTo('CLOSED');
      }
    } else if (this.state === 'CLOSED') {
      // Reset failure count on success
      this.failureCount = 0;
    }
  }

  /**
   * Record failed request
   */
  recordFailure(): void {
    if (this.state === 'HALF_OPEN') {
      // Failed during recovery test - reopen circuit
      this.transitionTo('OPEN');
    } else if (this.state === 'CLOSED') {
      this.failureCount++;
      if (this.failureCount >= this.options.failureThreshold) {
        this.transitionTo('OPEN');
      }
    }
  }

  /**
   * Get current circuit state
   */
  getState(): CircuitState {
    return this.state;
  }

  /**
   * Get failure count (for debugging)
   */
  getFailureCount(): number {
    return this.failureCount;
  }

  /**
   * Reset circuit breaker to CLOSED state
   */
  reset(): void {
    this.transitionTo('CLOSED');
  }

  /**
   * Subscribe to state changes
   */
  onStateChange(listener: (state: CircuitState) => void): () => void {
    this.stateChangeListeners.push(listener);
    return () => {
      this.stateChangeListeners = this.stateChangeListeners.filter((l) => l !== listener);
    };
  }

  /**
   * Transition to new state
   */
  private transitionTo(newState: CircuitState): void {
    const oldState = this.state;
    this.state = newState;

    // Reset counters based on state
    if (newState === 'CLOSED') {
      this.failureCount = 0;
      this.successCount = 0;
    } else if (newState === 'OPEN') {
      this.nextAttempt = Date.now() + this.options.resetTimeout;
      this.successCount = 0;
    } else if (newState === 'HALF_OPEN') {
      this.successCount = 0;
    }

    // Notify listeners
    if (oldState !== newState) {
      console.warn(`[CircuitBreaker] State transition: ${oldState} → ${newState}`, {
        failureCount: this.failureCount,
        successCount: this.successCount,
        nextAttempt: newState === 'OPEN' ? new Date(this.nextAttempt).toISOString() : null,
      });

      for (const listener of this.stateChangeListeners) {
        listener(newState);
      }
    }
  }
}

/**
 * Global circuit breaker instance for tRPC client
 */
export const globalCircuitBreaker = new CircuitBreaker({
  failureThreshold: 5, // Open after 5 consecutive failures
  resetTimeout: 30000, // Wait 30s before testing recovery
  successThreshold: 2, // Need 2 successes to close circuit
});
